// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useQuery } from '@tanstack/react-query';
import { userManagementApi, User as AuthUser } from '@tls/auth';
import { User } from 'model/User';
import { useCallback } from 'react';

// Only the fields this app actually owns are sent; omitted ones must stay untouched server-side.
type PartialAuthUser = Pick<AuthUser, 'userName' | 'roles'> & Partial<AuthUser>;

function toAuthUser(user: User): AuthUser {
  const mapped: PartialAuthUser = {
    userName: user.userName,
    roles: user.roles ?? [],
  };
  // Only send the id once it exists — putUser upserts and types id as required.
  if (user.id) mapped.id = user.id;
  if (user.firstName !== undefined) mapped.firstName = user.firstName;
  if (user.lastName !== undefined) mapped.lastName = user.lastName;
  if (user.email !== undefined) mapped.email = user.email;
  return mapped as AuthUser;
}

export default function useUsersApi() {
  const {
    data: users,
    isLoading: loadingUsers,
    refetch: getUsers,
  } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => userManagementApi.getUsers(),
  });

  // putUser upserts, so adding and editing are the same call
  const saveUser = useCallback(
    async (user: User) => {
      const result = await userManagementApi.putUser(toAuthUser(user));
      getUsers();
      return result;
    },
    [getUsers]
  );

  const deleteUser = useCallback(
    async (user: User) => {
      if (!user.id) return;
      const result = await userManagementApi.deleteUser(user.id);
      getUsers();
      return result;
    },
    [getUsers]
  );

  return { users, loadingUsers, getUsers, editUser: saveUser, addUser: saveUser, deleteUser };
}
